import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal/Modal";
import closeIcon from "../../assets/images/closeIcon.svg";
import { toast } from "react-toastify";
import { axiosBaseQuery } from "../../api/axiosBaseQuery";

// utility
import { sanitizeTextInput, validateValue } from "../../utils/textUtils";

const AddUserModal = ({ onClose, onAddUser, editData }) => {
    const [loading, setLoading] = useState(false);
    const [availableApps, setAvailableApps] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);

    // Temporary maps for easy lookup during submission
    const [appIdMap, setAppIdMap] = useState({});
    const [roleIdMap, setRoleIdMap] = useState({});
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mob: "",
        ugdn: "",
        role: "USER",
        selections: {}, // Structure: { [appName]: roleName }
    });

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const [appsRes, rolesRes] = await Promise.all([
                    axiosBaseQuery({ url: "/authorize/apps", method: "GET" }),
                    axiosBaseQuery({ url: "/authorize/roles", method: "GET" })
                ]);

                if (appsRes.error || rolesRes.error) {
                    throw new Error(appsRes.error?.message || rolesRes.error?.message || "Failed to load metadata");
                }

                const appsData = Array.isArray(appsRes.data) ? appsRes.data : [];
                const rolesData = Array.isArray(rolesRes.data) ? rolesRes.data : [];

                setAvailableApps(appsData);
                setAvailableRoles(rolesData);

                // Set default role if not editing and roles are available
                if (!editData && rolesData.length > 0) {
                    setFormData(prev => ({ ...prev, role: rolesData[0].role }));
                }

                // Create maps: name -> id
                const aMap = {};
                appsData.forEach(app => { if (app?.name) aMap[app.name] = app.id; });
                setAppIdMap(aMap);

                const rMap = {};
                rolesData.forEach(role => { if (role?.role) rMap[role.role] = role.id; });
                setRoleIdMap(rMap);
            } catch (error) {
                console.error("Error fetching metadata:", error);
                toast.error("Failed to load applications and roles.");
            }
        };

        fetchMetadata();

        if (editData) {
            setFormData({
                name: editData.name || "",
                email: editData.email || "",
                mob: editData.mob || "",
                ugdn: editData.ugdn || "",
                role: editData.role || "USER",
                selections: editData.selections || {},
                id: editData.app_user_id || editData.id // Ensure we track the correct ID
            });
        }
    }, [editData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let validatedValue = value;

        switch (name) {
            case "name":
                validatedValue = validateValue(value, "text");
                break;
            case "email":
                validatedValue = validateValue(value, "email");
                break;
            case "mob":
                validatedValue = validateValue(value, "mobile", 12);
                break;
            case "ugdn":
                validatedValue = validateValue(value, "numeric", 10);
                break;
            default:
                validatedValue = sanitizeTextInput(value);
        }

        setFormData((prev) => ({
            ...prev,
            [name]: validatedValue,
        }));
    };

    const handleAppToggle = (appName) => {
        setFormData((prev) => {
            const newSelections = { ...prev.selections };
            if (newSelections.hasOwnProperty(appName)) {
                delete newSelections[appName];
            } else {
                newSelections[appName] = ""; // Initialize with no role selected
            }
            return { ...prev, selections: newSelections };
        });
    };

    // eslint-disable-next-line no-unused-vars
    const handleRoleSelect = (appName, roleName) => {
        setFormData((prev) => ({
            ...prev,
            selections: {
                ...prev.selections,
                [appName]: roleName
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Application selection is now optional
        const selectedAppNames = Object.keys(formData.selections);

        setLoading(true);

        try {
            if (editData) {
                // 1. Synchronize App-Role Mappings
                const selectionsPayload = {
                    selections: Object.entries(formData.selections).map(([appName, roleName]) => ({
                        app_id: appIdMap[appName],
                        role_id: roleIdMap[roleName]
                    })),
                    updated_by: 1
                };

                const mappingRes = await axiosBaseQuery({
                    url: `/authorize/mapping/user/${editData.id}`,
                    method: "POST",
                    body: selectionsPayload
                });

                if (mappingRes.error) throw mappingRes.error;

                // 2. Update User Profile
                const userPayload = {
                    full_name: formData.name,
                    username: formData.email,
                    mobile_no: formData.mob,
                    ugdn_id: formData.ugdn,
                    role: formData.role.toUpperCase().replace(/\s+/g, ''),
                    active: true,
                    updated_by: 1
                };

                const profileRes = await axiosBaseQuery({
                    url: `/app-users/${editData.id}`,
                    method: "POST",
                    body: userPayload
                });

                if (profileRes.error) {
                    console.warn("Profile update failed:", profileRes.error);
                }

                toast.info("User and Role Mappings updated successfully");
                onAddUser({
                    ...formData,
                    id: formData.id || editData.id, // Use the ID we tracked
                    appName: selectedAppNames.join(", "),
                    role: formData.role
                });
            } else {
                // 1. Create App User (Base user)
                const userPayload = {
                    full_name: formData.name,
                    username: formData.email,
                    mobile_no: formData.mob,
                    ugdn_id: formData.ugdn,
                    role: formData.role.toUpperCase().replace(/\s+/g, ''), // Default internal role
                    active: true,
                    created_by: 1
                };

                const userResponse = await axiosBaseQuery({
                    url: "/app-users",
                    method: "POST",
                    body: userPayload
                });

                if (userResponse.error) throw userResponse.error;

                const responseData = userResponse.data;
                const newUserId = responseData?.app_user_id || responseData?.id;

                // 2. Map User to Apps and their selected Roles
                for (const appName of selectedAppNames) {
                    const roleName = formData.selections[appName];
                    const mappingPayload = {
                        app_user_id: newUserId,
                        role_id: roleIdMap[roleName],
                        app_id: appIdMap[appName],
                        is_enabled: true,
                        created_by: 1
                    };
                    const mappingRes = await axiosBaseQuery({
                        url: "/authorize/mapping",
                        method: "POST",
                        body: mappingPayload
                    });
                    if (mappingRes.error) throw mappingRes.error;
                }

                toast.info("User and all Role Mappings created successfully!");
                onAddUser({
                    ...formData,
                    id: newUserId,
                    appName: selectedAppNames.join(", "),
                    role: formData.role
                });
            }
            onClose();
        } catch (error) {
            console.error("Error in API calls:", error);
            const errorMessage = error.message || "Failed to process request";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    const [searchTerm, setSearchTerm] = useState("");

    const filteredApps = availableApps.filter(app =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRoleToggle = (appName, roleName) => {
        setFormData((prev) => {
            const newSelections = { ...prev.selections };
            if (newSelections[appName] === roleName) {
                delete newSelections[appName];
            } else {
                newSelections[appName] = roleName;
            }
            return { ...prev, selections: newSelections };
        });
    };

    return (
        <Modal open={true} onClose={onClose}>
            <div className="modal-content refined-modal">
                <div className="modal-header">
                    <h2>{editData ? "Edit User" : "Add New User"}</h2>
                    <button className="close-btn" onClick={onClose}>
                        <img src={closeIcon} alt="close" />
                    </button>
                </div>
                <form className="modal-form" onSubmit={handleSubmit}>
                    <div className="modal-section">
                        <h3 className="section-title">USER INFORMATION</h3>
                        <div className="user-info-grid">
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter full name"
                                    required
                                    disabled={!!editData}
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="user@example.com"
                                    required
                                    disabled={!!editData}
                                />
                            </div>
                            {/* <div className="form-group">
                                <label>Mobile</label>
                                <input
                                    type="text"
                                    name="mob"
                                    value={formData.mob}
                                    onChange={handleChange}
                                    placeholder="+91"
                                    maxLength={12}
                                    required
                                    disabled={!!editData}
                                />
                            </div> */}
                            <div className="form-group">
                                <label>UGDN</label>
                                <input
                                    type="text"
                                    name="ugdn"
                                    value={formData.ugdn}
                                    onChange={handleChange}
                                    placeholder="Enter UGDN"
                                    maxLength={12}
                                    required
                                    disabled={!!editData}
                                />
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <div className="role-toggle-group">
                                    {availableRoles.map(role => (
                                        <button
                                            key={role.id}
                                            type="button"
                                            className={`role-toggle-btn ${(formData.role || "").toUpperCase() === (role.role || "").toUpperCase() ? "active" : ""}`}
                                            onClick={() => setFormData(prev => ({ ...prev, role: role.role }))}
                                        >
                                            {(role.role || "").toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-section">
                        <h3 className="section-title">SELECTED APPLICATIONS</h3>
                        <div className="selected-apps-chips">
                            {Object.keys(formData.selections).length > 0 ? (
                                Object.entries(formData.selections).map(([appName, roleName]) => (
                                    <div key={appName} className="app-chip">
                                        <span className="chip-text">
                                            {appName} <span className="chip-role">{roleName.toUpperCase()}</span>
                                        </span>
                                        <span
                                            className="chip-remove"
                                            onClick={() => handleAppToggle(appName)}
                                        >×</span>
                                    </div>
                                ))
                            ) : (
                                <p className="no-selection-text">No applications selected</p>
                            )}
                        </div>
                    </div>

                    <div className="modal-section">
                        <div className="section-header-row">
                            <h3 className="section-title">APPLICATION ACCESS</h3>
                            <div className="search-container">
                                <input
                                    type="text"
                                    placeholder="Search applications..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="app-search-input"
                                />
                            </div>
                        </div>

                        <div className="app-access-table-container">
                            <table className="app-access-table">
                                <thead>
                                    <tr>
                                        <th>APPLICATION</th>
                                        <th className="role-column-header">ROLE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredApps.map(app => (
                                        <tr key={app.id}>
                                            <td>
                                                <div className="app-details">
                                                    <span className="app-main-name">{app.name}</span>
                                                    <span className="app-subtitle">{app.description || `Access to ${app.name.toLowerCase()} features`}</span>
                                                </div>
                                            </td>
                                            <td className="role-actions-cell">
                                                <div className="role-toggle-group">
                                                    {availableRoles.map(role => (
                                                        <button
                                                            key={role.id}
                                                            type="button"
                                                            className={`role-toggle-btn ${(formData.selections[app.name] || "").toUpperCase() === (role.role || "").toUpperCase() ? "active" : ""}`}
                                                            onClick={() => handleRoleToggle(app.name, role.role)}
                                                        >
                                                            {(role.role || "").toUpperCase()}
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="modal-footer refined-footer">
                        <button type="button" className="cancel-action-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="submit-btn orange-btn" disabled={loading}>
                            {loading ? "Saving..." : (editData ? "Save" : "Add User")}
                        </button>
                    </div>
                </form >
            </div >
        </Modal >
    );
};

export default AddUserModal;
